import { useEffect, useRef, useState } from "react";
import { Plus, Save, Loader2, Trash2, RefreshCw, Star, Eye, EyeOff, Upload, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

const card = "rounded-2xl border border-white/10 bg-surface/60 p-5";
const inp = "mt-1 w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold";
const lbl = (t) => <span className="text-[11px] uppercase tracking-widest text-white/40">{t}</span>;
const btnGold = "inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-xs font-bold uppercase text-black disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-bold uppercase text-white/70";

export function UploadInput({ label, value, onChange, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    const form = new FormData(); form.append("file", f);
    try { const r = await api.post("/admin/upload", form); onChange(r.data.url); } catch {}
    setBusy(false); e.target.value = "";
  };
  return (
    <label className="block">{lbl(label)}
      <div className="mt-1 flex items-center gap-2">
        {value && <img src={value} alt="" className="h-10 w-10 rounded-md border border-white/10 object-cover" />}
        <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload" className="w-full rounded-lg border border-white/15 bg-ink/60 px-3 py-2 text-sm text-white outline-none focus:border-gold" />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-3 py-2 text-[11px] font-bold uppercase text-gold disabled:opacity-60">{busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload</button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={up} />
      </div>
    </label>
  );
}

function useReleases() {
  const [releases, setReleases] = useState([]);
  useEffect(() => { api.get("/admin/releases").then((r) => setReleases(r.data)).catch(() => {}); }, []);
  return releases;
}

/* ------------------------- VIDEOS ------------------------- */
export function VideosAdmin() {
  const [rows, setRows] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState("");
  const [editing, setEditing] = useState(null);
  const releases = useReleases();
  const load = () => api.get("/admin/videos").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setSyncing(true); setMsg("");
    try { const r = await api.post("/admin/videos/sync-youtube"); setMsg(`Found ${r.data.found}, imported ${r.data.imported} new video(s).`); }
    catch (e) { setMsg(e?.response?.data?.detail || "Sync failed"); }
    setSyncing(false); load();
  };
  const patch = async (id, body) => { await api.patch(`/admin/videos/${id}`, body).catch(() => {}); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this video?")) return; await api.delete(`/admin/videos/${id}`).catch(() => {}); load(); };
  const relTitle = (id) => releases.find((r) => r.internalId === id)?.title;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Videos <span className="text-gold">({rows.length})</span></h2><p className="mt-1 text-xs text-white/40">Imported from your YouTube channel. Link each video to its song so pages cross-promote.</p></div>
        <div className="flex gap-2">
          <button data-testid="sync-youtube" onClick={sync} disabled={syncing} className={btnGold}>{syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync YouTube</button>
          <button data-testid="new-video" onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold uppercase text-gold"><Plus size={14} /> Add</button>
        </div>
      </div>
      {msg && <p data-testid="sync-message" className="mb-4 text-sm text-gold">{msg}</p>}
      {editing && !editing.internalId && <VideoForm initial={{}} releases={releases} onDone={() => { setEditing(null); load(); }} />}
      <div className="mt-4 space-y-3">
        {rows.map((v) => (
          <div key={v.internalId} className={card} data-testid={`video-row-${v.slug}`}>
            {editing?.internalId === v.internalId ? <VideoForm initial={v} releases={releases} onDone={() => { setEditing(null); load(); }} /> : (
              <div className="flex flex-wrap items-center gap-4">
                <img src={v.thumbnail} alt="" className="h-14 w-24 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{v.title} {v.featured && <span className="ml-1 rounded-full bg-gold px-2 py-0.5 text-[9px] font-bold uppercase text-black">Featured</span>}{v.active === false && <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[9px] uppercase text-white/60">Hidden</span>}</p>
                  <p className="text-[11px] uppercase tracking-widest text-white/40">{v.videoType} • {v.publishedAt?.slice(0, 10)} • {relTitle(v.releaseId) ? <span className="text-gold">♪ {relTitle(v.releaseId)}</span> : <span className="text-red-300/70">no song linked</span>}</p>
                  <a href={`/videos/${v.slug}`} target="_blank" rel="noreferrer" className="text-[11px] text-white/40 hover:text-gold">/videos/{v.slug} <ExternalLink size={10} className="inline" /></a>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`video-feature-${v.slug}`} onClick={() => patch(v.internalId, { featured: !v.featured })} className={btnGhost}><Star size={12} /></button>
                  <button onClick={() => patch(v.internalId, { active: v.active === false })} className={btnGhost}>{v.active === false ? <Eye size={12} /> : <EyeOff size={12} />}</button>
                  <button data-testid={`video-edit-${v.slug}`} onClick={() => setEditing(v)} className={btnGhost}>Edit</button>
                  <button onClick={() => remove(v.internalId)} className="rounded-full border border-red-400/30 px-3 py-1.5 text-[11px] text-red-300"><Trash2 size={12} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-white/40">No videos yet. Click “Sync YouTube” to import your channel.</p>}
      </div>
    </div>
  );
}

function VideoForm({ initial, releases, onDone }) {
  const [f, setF] = useState({ title: "", youtubeId: "", videoType: "music_video", description: "", releaseId: "", publishedAt: "", ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    const yt = (f.youtubeId || "").match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{11})/)?.[1] || f.youtubeId;
    const body = { title: f.title, youtubeId: yt, videoType: f.videoType, description: f.description || null, releaseId: f.releaseId || null, publishedAt: f.publishedAt || null };
    try {
      if (initial.internalId) await api.patch(`/admin/videos/${initial.internalId}`, body);
      else await api.post("/admin/videos", body);
      onDone();
    } catch {}
    setSaving(false);
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="video-form">
      <label>{lbl("Title")}<input data-testid="video-title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></label>
      <label>{lbl("YouTube URL or ID")}<input data-testid="video-youtube" value={f.youtubeId} onChange={(e) => set("youtubeId", e.target.value)} className={inp} /></label>
      <label>{lbl("Type")}<select value={f.videoType} onChange={(e) => set("videoType", e.target.value)} className={inp}>{["music_video", "lyric", "visualizer", "short", "promo", "live"].map((t) => <option key={t}>{t}</option>)}</select></label>
      <label>{lbl("Linked song")}<select data-testid="video-release" value={f.releaseId || ""} onChange={(e) => set("releaseId", e.target.value)} className={inp}><option value="">— none —</option>{releases.map((r) => <option key={r.internalId} value={r.internalId}>{r.title}</option>)}</select></label>
      <label className="sm:col-span-2">{lbl("Description")}<textarea rows={3} value={f.description || ""} onChange={(e) => set("description", e.target.value)} className={inp} /></label>
      <div className="flex gap-2 sm:col-span-2">
        <button data-testid="video-save" onClick={save} disabled={saving || !f.title || !f.youtubeId} className={btnGold}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <button onClick={onDone} className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase text-white/60">Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------- NEWS ------------------------- */
export function NewsAdmin() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const releases = useReleases();
  const load = () => api.get("/admin/posts").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Delete this story?")) return; await api.delete(`/admin/posts/${id}`).catch(() => {}); load(); };
  const toggle = async (p) => { await api.patch(`/admin/posts/${p.internalId}`, { published: !p.published }).catch(() => {}); load(); };
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">News & Stories</h2><p className="mt-1 text-xs text-white/40">Release announcements, behind-the-song stories, milestones, press. Quality over volume.</p></div>
        <button data-testid="new-post" onClick={() => setEditing({})} className={btnGold}><Plus size={14} /> New Story</button>
      </div>
      {editing && !editing.internalId && <PostForm initial={{}} releases={releases} onDone={() => { setEditing(null); load(); }} />}
      <div className="mt-4 space-y-3">
        {rows.map((p) => (
          <div key={p.internalId} className={card} data-testid={`post-row-${p.slug}`}>
            {editing?.internalId === p.internalId ? <PostForm initial={p} releases={releases} onDone={() => { setEditing(null); load(); }} /> : (
              <div className="flex flex-wrap items-center gap-4">
                {p.coverImage && <img src={p.coverImage} alt="" className="h-14 w-20 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-white">{p.title} {!p.published && <span className="ml-1 rounded-full bg-white/10 px-2 py-0.5 text-[9px] uppercase text-white/60">Draft</span>}</p>
                  <p className="text-[11px] uppercase tracking-widest text-white/40">{p.category} • {p.publishedAt?.slice(0, 10)} • /news/{p.slug}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`post-publish-${p.slug}`} onClick={() => toggle(p)} className={btnGhost}>{p.published ? "Unpublish" : "Publish"}</button>
                  <button onClick={() => setEditing(p)} className={btnGhost}>Edit</button>
                  <button data-testid={`post-delete-${p.slug}`} onClick={() => remove(p.internalId)} className="rounded-full border border-red-400/30 px-3 py-1.5 text-[11px] text-red-300"><Trash2 size={12} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-white/40">No stories yet.</p>}
      </div>
    </div>
  );
}

function PostForm({ initial, releases, onDone }) {
  const [f, setF] = useState({ title: "", slug: "", excerpt: "", body: "", coverImage: "", category: "news", releaseId: "", published: false, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    const body = { title: f.title, slug: f.slug || null, excerpt: f.excerpt || null, body: f.body, coverImage: f.coverImage || null, category: f.category, releaseId: f.releaseId || null, published: !!f.published };
    try {
      if (initial.internalId) await api.patch(`/admin/posts/${initial.internalId}`, body);
      else await api.post("/admin/posts", body);
      onDone();
    } catch {}
    setSaving(false);
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="post-form">
      <label>{lbl("Title")}<input data-testid="post-title-input" value={f.title} onChange={(e) => set("title", e.target.value)} className={inp} /></label>
      <label>{lbl("Slug (optional)")}<input value={f.slug || ""} onChange={(e) => set("slug", e.target.value)} className={inp} /></label>
      <label>{lbl("Category")}<select value={f.category} onChange={(e) => set("category", e.target.value)} className={inp}>{["news", "release", "video", "story", "press", "live", "milestone"].map((t) => <option key={t}>{t}</option>)}</select></label>
      <label>{lbl("Related song")}<select value={f.releaseId || ""} onChange={(e) => set("releaseId", e.target.value)} className={inp}><option value="">— none —</option>{releases.map((r) => <option key={r.internalId} value={r.internalId}>{r.title}</option>)}</select></label>
      <div className="sm:col-span-2"><UploadInput label="Cover image" value={f.coverImage} onChange={(v) => set("coverImage", v)} /></div>
      <label className="sm:col-span-2">{lbl("Excerpt (1–2 sentences · used in search results & cards)")}<textarea rows={2} value={f.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} className={inp} /></label>
      <label className="sm:col-span-2">{lbl("Body (blank line between paragraphs)")}<textarea data-testid="post-body-input" rows={10} value={f.body || ""} onChange={(e) => set("body", e.target.value)} className={inp} /></label>
      <label className="flex items-center gap-2 text-sm text-white/70"><input data-testid="post-published" type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} /> Published</label>
      <div className="flex gap-2 sm:col-span-2">
        <button data-testid="post-save" onClick={save} disabled={saving || !f.title} className={btnGold}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <button onClick={onDone} className="rounded-full border border-white/15 px-4 py-2 text-xs font-bold uppercase text-white/60">Cancel</button>
      </div>
    </div>
  );
}

/* ------------------------- FAN POLL + SOCIAL POSTS + INQUIRIES ------------------------- */
export function EngagementAdmin() {
  const [polls, setPolls] = useState([]);
  const [social, setSocial] = useState([]);
  const [inq, setInq] = useState([]);
  const [pf, setPf] = useState({ question: "What should DJ Código drop next?", options: "New single\nMusic video\nRemix\nLive DJ set" });
  const [sf, setSf] = useState({ platform: "tiktok", url: "", thumbnail: "", caption: "" });
  const load = () => Promise.all([api.get("/admin/polls"), api.get("/admin/social-posts"), api.get("/admin/inquiries")])
    .then(([p, s, i]) => { setPolls(p.data); setSocial(s.data); setInq(i.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const createPoll = async () => { await api.post("/admin/polls", { question: pf.question, options: pf.options.split("\n"), active: true }).catch(() => {}); load(); };
  const togglePoll = async (id) => { await api.post(`/admin/polls/${id}/toggle`).catch(() => {}); load(); };
  const delPoll = async (id) => { if (!window.confirm("Delete poll and its votes?")) return; await api.delete(`/admin/polls/${id}`).catch(() => {}); load(); };
  const createSocial = async () => { if (!sf.url) return; await api.post("/admin/social-posts", sf).catch(() => {}); setSf({ platform: "tiktok", url: "", thumbnail: "", caption: "" }); load(); };
  const delSocial = async (id) => { await api.delete(`/admin/social-posts/${id}`).catch(() => {}); load(); };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-4 font-display text-2xl font-bold uppercase tracking-tight text-white">Fan Vote</h2>
        <div className={card} data-testid="poll-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <label>{lbl("Question")}<input data-testid="poll-question-input" value={pf.question} onChange={(e) => setPf({ ...pf, question: e.target.value })} className={inp} /></label>
            <label>{lbl("Options (one per line)")}<textarea data-testid="poll-options-input" rows={4} value={pf.options} onChange={(e) => setPf({ ...pf, options: e.target.value })} className={inp} /></label>
          </div>
          <button data-testid="poll-create" onClick={createPoll} className={`${btnGold} mt-3`}><Plus size={14} /> Launch poll (replaces active)</button>
        </div>
        <div className="mt-3 space-y-3">
          {polls.map((p) => (
            <div key={p.internalId} className={`${card} ${p.active ? "border-gold/40" : ""}`} data-testid={`poll-row-${p.internalId}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-white">{p.question} {p.active && <span className="ml-1 rounded-full bg-gold px-2 py-0.5 text-[9px] font-bold uppercase text-black">Live</span>}</p>
                <div className="flex gap-2"><button onClick={() => togglePoll(p.internalId)} className={btnGhost}>{p.active ? "Close" : "Activate"}</button><button onClick={() => delPoll(p.internalId)} className="rounded-full border border-red-400/30 px-3 py-1.5 text-[11px] text-red-300"><Trash2 size={12} /></button></div>
              </div>
              <div className="mt-2 space-y-1">{p.options.map((o, i) => <div key={i} className="flex justify-between text-xs text-white/70"><span>{o.label}</span><span>{o.votes} ({o.pct}%)</span></div>)}</div>
              <p className="mt-1 text-[11px] text-white/40">{p.total} votes</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-1 font-display text-2xl font-bold uppercase tracking-tight text-white">Inside the Código</h2>
        <p className="mb-4 text-xs text-white/40">Paste links to your best TikToks / Reels / Shorts with a screenshot thumbnail. Shown on the homepage as lightweight cards.</p>
        <div className={card}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label>{lbl("Platform")}<select value={sf.platform} onChange={(e) => setSf({ ...sf, platform: e.target.value })} className={inp}>{["tiktok", "instagram", "youtube_short", "facebook"].map((p) => <option key={p}>{p}</option>)}</select></label>
            <label>{lbl("Post URL")}<input data-testid="social-url" value={sf.url} onChange={(e) => setSf({ ...sf, url: e.target.value })} className={inp} /></label>
            <UploadInput label="Thumbnail (screenshot)" value={sf.thumbnail} onChange={(v) => setSf({ ...sf, thumbnail: v })} />
            <label>{lbl("Caption")}<input value={sf.caption} onChange={(e) => setSf({ ...sf, caption: e.target.value })} className={inp} /></label>
          </div>
          <button data-testid="social-add" onClick={createSocial} className={`${btnGold} mt-3`}><Plus size={14} /> Add post</button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-6">
          {social.map((s) => (
            <div key={s.internalId} className="relative aspect-[9/16] overflow-hidden rounded-xl border border-white/10 bg-ink/60">
              {s.thumbnail && <img src={s.thumbnail} alt="" className="h-full w-full object-cover" />}
              <span className="absolute left-1 top-1 rounded-full bg-black/60 px-1.5 text-[8px] uppercase text-gold">{s.platform}</span>
              <button onClick={() => delSocial(s.internalId)} className="absolute bottom-1 right-1 rounded-full bg-black/70 p-1 text-red-300"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-4 font-display text-2xl font-bold uppercase tracking-tight text-white">Inbox <span className="text-gold">({inq.length})</span></h2>
        <div className={`${card} overflow-x-auto`}>
          <table data-testid="inbox-table" className="w-full min-w-[560px] text-left text-sm">
            <thead><tr className="text-[11px] uppercase tracking-widest text-white/40"><th className="pb-2">Date</th><th>Type</th><th>Name</th><th>Email</th><th>Message</th></tr></thead>
            <tbody>{inq.map((i) => <tr key={i.id} className="border-t border-white/5 align-top text-white/80"><td className="py-2">{i.createdAt?.slice(0, 10)}</td><td className="capitalize text-gold">{i.type}</td><td>{i.name}{i.organization ? <span className="block text-[11px] text-white/40">{i.organization}</span> : null}</td><td><a href={`mailto:${i.email}`} className="hover:text-gold">{i.email}</a></td><td className="max-w-xs whitespace-pre-line text-xs text-white/60">{i.message}</td></tr>)}</tbody>
          </table>
          {inq.length === 0 && <p className="text-sm text-white/40">No booking / press inquiries yet.</p>}
        </div>
      </div>
    </div>
  );
}
